import { OfferFormSchema } from '@/components/shared/OfferFormContext/schema';
import {
  OFFER_EXPIRY_TYPES,
  OFFER_INVALID_REASON,
  OFFER_LOCALE,
  OFFER_MULTIPLE_OPTIONS_ADDON_DISCOUNT_ENTRY_TYPE,
  OFFER_MULTIPLE_OPTIONS_ADDON_ENTRY_FREQUENCY,
  OFFER_MULTIPLE_OPTIONS_PLAN,
  OFFER_MULTIPLE_OPTIONS_PRODUCT_CATEGORY,
  OFFER_PLAN,
  OFFER_MEDIA_DEVICE_TYPE,
  OFFER_PRODUCT_USAGE,
  OFFER_SINGLE_OPTION_DISCOUNT_ENTRY_TYPE,
  OFFER_SINGLE_OPTION_DISCOUNT_TYPE,
  OFFER_SINGLE_OPTION_PLAN,
  OFFER_SINGLE_OPTION_PRODUCT_CATEGORY,
  OFFER_STATUS,
  OFFER_TRANSACTION
} from '@/constants/offer';
import { FormField } from '@/utils/form/baseForm';

import { User } from '..';

// -------- constants & configs ---------

export type OfferLocaleType = (typeof OFFER_LOCALE)[keyof typeof OFFER_LOCALE];

export type OfferStatusType = (typeof OFFER_STATUS)[keyof typeof OFFER_STATUS];

export type OfferExpiryType = (typeof OFFER_EXPIRY_TYPES)[keyof typeof OFFER_EXPIRY_TYPES];

export type OfferTransactionType = (typeof OFFER_TRANSACTION)[keyof typeof OFFER_TRANSACTION];

export type OfferProductUsageType = (typeof OFFER_PRODUCT_USAGE)[keyof typeof OFFER_PRODUCT_USAGE];

export type OfferPlanType = (typeof OFFER_PLAN)[keyof typeof OFFER_PLAN];

export type OfferMultipleOptionsPlanType =
  (typeof OFFER_MULTIPLE_OPTIONS_PLAN)[keyof typeof OFFER_MULTIPLE_OPTIONS_PLAN];

export type OfferSingleOptionPlanType =
  (typeof OFFER_SINGLE_OPTION_PLAN)[keyof typeof OFFER_SINGLE_OPTION_PLAN];

export type OfferMultipleOptionsProductCategory =
  (typeof OFFER_MULTIPLE_OPTIONS_PRODUCT_CATEGORY)[keyof typeof OFFER_MULTIPLE_OPTIONS_PRODUCT_CATEGORY];

export type OfferSingleOptionProductCategory =
  (typeof OFFER_SINGLE_OPTION_PRODUCT_CATEGORY)[keyof typeof OFFER_SINGLE_OPTION_PRODUCT_CATEGORY];

export type OfferSingleOptionDiscountType =
  (typeof OFFER_SINGLE_OPTION_DISCOUNT_TYPE)[keyof typeof OFFER_SINGLE_OPTION_DISCOUNT_TYPE];

export type OfferMultipleOptionsAddonEntryFrequencyType =
  (typeof OFFER_MULTIPLE_OPTIONS_ADDON_ENTRY_FREQUENCY)[keyof typeof OFFER_MULTIPLE_OPTIONS_ADDON_ENTRY_FREQUENCY];

export type OfferMultipleOptionsAddonDiscountEntryType =
  (typeof OFFER_MULTIPLE_OPTIONS_ADDON_DISCOUNT_ENTRY_TYPE)[keyof typeof OFFER_MULTIPLE_OPTIONS_ADDON_DISCOUNT_ENTRY_TYPE];

export type OfferSingleOptionDiscountEntryType =
  (typeof OFFER_SINGLE_OPTION_DISCOUNT_ENTRY_TYPE)[keyof typeof OFFER_SINGLE_OPTION_DISCOUNT_ENTRY_TYPE];

export type OfferMediaDeviceType =
  (typeof OFFER_MEDIA_DEVICE_TYPE)[keyof typeof OFFER_MEDIA_DEVICE_TYPE];

export type OfferInvalidReasonType =
  (typeof OFFER_INVALID_REASON)[keyof typeof OFFER_INVALID_REASON];

// ------------ products specific --------------

export type CompleteCoreProduct = CoreProduct & {
  addons: AddonProduct[];
};

export interface CoreProduct {
  id: number;
  created_at: Date;
  name: string;
  // locale: OfferLocaleType;
  is_onetime_purchase: boolean;
  offer_product_type_id: number;
  product_data: ShopifyProductData<'subscription'>;
  media: OfferCoreProductMedia[];
  quantity: number;
  sort: number;
}

export interface OfferCoreProductMedia {
  id: number;
  created_at: Date;
  offer_subscription_type_id: number;
  type: OfferMediaDeviceType;
  sanity_reference_id: string;
  product_category: OfferMultipleOptionsProductCategory;
}

export interface AddonProduct {
  id: number;
  created_at: Date;
  name: string;
  // locale: OfferLocaleType;
  is_first_order_only: boolean;
  offer_product_type_id: number;
  offer_subscription_type_id: number;
  product_data: ShopifyProductData<'addon'>;
  media: OfferAddonProductMedia;
  quantity: number;
  sort: number;
}

export interface OfferAddonProductMedia {
  id: number;
  created_at: Date;
  type: OfferMediaDeviceType;
  sanity_reference_id: string;
}

export interface ShopifyProductData<T> {
  id: number;
  created_at: Date;
  name: string;
  locale: OfferLocaleType;
  category: OfferMultipleOptionsProductCategory;
  shopify_product_variant_uri: string;
  description: string | null;
  intended_usages: [T, ...OfferProductUsageType[]];
}

export type DbProductShopifyData<T> = {
  product_data: ShopifyProductData<T>;
};

// ------------ multiple options specific --------------

export enum OfferMultipleOptionsCoreProductIds {
  PouchSingleSubscription = 1,
  PouchDoubleSubscription = 2,
  TravelPackSingleSubscription = 3,
  TravelPackDoubleSubscription = 4,
  PouchOneTimePurchase = 5,
  TravelPackOneTimePurchase = 6,
  PouchFamilyPlan = 7
}

export type OfferMultipleOptionsCoreProduct = CoreProduct;

export type OfferMultipleOptionsAddonProduct = AddonProduct;

export interface OfferMultipleOptionsCoreProductSelectionConfig {
  id: number;
  created_at: Date;
  offer_id: number;
  product_id: number;
  customization: OfferMultipleOptionsCoreProductSelectionCustomization;
  addons: OfferMultipleOptionsAddonProductSelectionConfig[];
  media: OfferMultipleOptionsCoreProductMedia[];
  enable_flavors: boolean;
  locale: OfferLocaleType;
  sort: number;
}

export interface OfferMultipleOptionsCoreProductMedia {
  id: number;
  created_at: Date;
  offer_subscription_id: number;
  type: OfferMediaDeviceType;
  sanity_reference_id: string;
}

export interface OfferMultipleOptionsCoreProductSelectionCustomization {
  valueTitle?: string | null;
  subTitle: string | null;
  subDescription: string | null;
  subBullets: string[] | null;
  badge: string | null;
  [key: string]: unknown;
}

export interface OfferMultipleOptionsFreeTrialProductSelectionConfig {
  id: number;
  created_at: Date;
  offer_id: number;
  product_id: number;
  // TODO: finalize interface structure
}

export interface OfferMultipleOptionsAddonProductSelectionConfig {
  id: number;
  created_at: Date;
  offer_subscription_id: number;
  product_id: number;
  frequency?: OfferMultipleOptionsAddonEntryFrequencyType;
  discount_type: OfferMultipleOptionsAddonDiscountEntryType;
  discount_amount: number | null;
  quantity?: number;
  name?: string;
}

export interface OfferMultipleOptionsCoreProductSelectionMedia {
  id: number;
  created_at: Date;
  offer_id: number;
  product_category: OfferMultipleOptionsProductCategory;
  sanity_reference_id: string;
  type: OfferMediaDeviceType;
}

// --------- single option specific -----------

export type OfferSingleOptionProduct = CoreProduct | AddonProduct;

export interface OfferSingleOptionProductSelectionConfig {
  id: number;
  created_at: Date;
  offer_id: number;
  product_id: number;
  product_type_id: number;
  locale: OfferLocaleType;
  sort: number;
  intended_usage: OfferProductUsageType;
  // TODO: expand this to actual Single Option products
}

// single option product discount uses `product_type_id` rather than `product_id`
// as reference to the product since the DB table column uses `product_type_id`
export interface OfferSingleOptionDiscountConfigEntry {
  type: OfferSingleOptionDiscountEntryType;
  amount: number;
  product_type_id: number | null;
}

export interface OfferSingleOptionMedia {
  id: number;
  created_at: Date;
  offer_id: number;
  sanity_reference_id: string | null;
}

export interface OfferSingleOptionHeroMedia {
  desktop: OfferSingleOptionMedia | null;
  mobile: OfferSingleOptionMedia | null;
}

// -------------- common --------------

export type OfferFormFieldEntries<T extends keyof OfferFormSchema['shape']> = {
  [Key in T]: FormField<OfferFormSchema['shape'][Key], Key>;
};

export interface OfferCustomContent {
  id: number;
  offer_id: number;
  version: number;
  is_current: boolean;
  content_id: string;
  content: string;
}

export type OfferGlobalCustomContentMap = Record<Partial<OfferLocaleType>, OfferCustomContent[]>;

export interface OfferAvailabilityRestriction {
  id: number;
  created_at: Date;
  offer_id: number;
  quantity: number | null;
  date_from: Date | null;
  date_to: Date | null;
  is_sold_out: boolean;
  locales: OfferLocaleType[];
}

export interface OfferAllowedPages {
  created_at: Date;
  id: number;
  is_active: boolean;
  offer_id: number;
  page_id: number;
}

export interface OfferReview {
  id: number;
  created_at: Date;
  offer_id: number;
  is_active: boolean;
  last_approval_at: Date | null;
}

export interface OfferReviewUser {
  id: number;
  created_at: Date;
  offer_review_id: number;
  user_id: string;
  has_approved: boolean;

  user: User | null;
}

export interface Offer {
  // ---------- global ----------
  id: number;
  name: string;
  created_at: Date;
  created_by_user: User;
  created_by_user_id: string;

  status: OfferStatusType;
  is_paused: boolean;
  is_base_offer: boolean;

  // ---------- step 1 ----------
  headline: string;
  transaction_type: OfferTransactionType;
  is_free_shipping: boolean;
  is_limited_by_address: boolean;
  type: OfferPlanType;

  // multiple options offer (customer's choice)
  // multiple_options_offer_plan: OfferMultipleOptionsPlanType | null;
  multiple_options_free_trial_product_selections_config: Record<
    Partial<OfferLocaleType>,
    OfferMultipleOptionsFreeTrialProductSelectionConfig[]
  >;
  multiple_options_rebill_days: Record<Partial<OfferLocaleType>, number> | null;
  multiple_options_core_product_selections_config: Record<
    Partial<OfferLocaleType>,
    OfferMultipleOptionsCoreProductSelectionConfig[]
  >;
  hide_unselected_subscriptions_from_checkout: boolean;
  core_product_subscription_type_defaults: {
    pouches_default_subscription_type_id: OfferMultipleOptionsCoreProductIds | null;
    travel_pack_default_subscription_type_id: OfferMultipleOptionsCoreProductIds | null;
  };
  multiple_options_core_product_fallback_media: Record<
    Partial<OfferLocaleType>,
    OfferMultipleOptionsCoreProductSelectionMedia[]
  >;

  // single option offer (direct checkout)
  // single_option_offer_plan: OfferSingleOptionPlanType | null;
  single_option_product_selections_config: Record<
    Partial<OfferLocaleType>,
    OfferSingleOptionProductSelectionConfig[]
  >;
  single_option_discount_type: Record<Partial<OfferLocaleType>, OfferSingleOptionDiscountType>;
  single_option_discount_config: Record<
    Partial<OfferLocaleType>,
    OfferSingleOptionDiscountConfigEntry[]
  >;
  single_option_hero_media: Record<Partial<OfferLocaleType>, OfferSingleOptionHeroMedia>;

  // ---------- step 2 ----------
  is_page_restriction_enabled: boolean;
  availability_restrictions: OfferAvailabilityRestriction[] | null;
}
